import axios from "axios";
import { notify } from "modules/notify";
import { getCookie, setCookie } from "modules/cookie";

function findFirstUnfinishedScreenIndex(screens) {
  if (screens.every(s => s.finished)) {
    return screens.length - 1;
  }

  return screens.findIndex(s => !s.finished);
}

function getScreenIndexCookieName(questSlug) {
  return `sqlh_quest_${questSlug}_screen_index`;
}

function saveScreenIndexToCookie(questSlug, screenIndex) {
  const cookieName = getScreenIndexCookieName(questSlug);
  setCookie(cookieName, screenIndex, 30);
}

function loadScreenIndexFromCookie(questSlug) {
  const cookieName = getScreenIndexCookieName(questSlug);
  const savedIndex = getCookie(cookieName);
  return savedIndex !== null ? parseInt(savedIndex, 10) : null;
}

function getQueriesLocalStorageKey(questId) {
  return `sqlh:quest_${questId}:user_queries`;
}

function saveQueriesToLocalStorage(questId, queriesById) {
  try {
    const key = getQueriesLocalStorageKey(questId);
    localStorage.setItem(key, JSON.stringify(queriesById));
  } catch (error) {
    console.error('Failed to save queries to localStorage:', error);
  }
}

function loadQueriesFromLocalStorage(questId) {
  try {
    const key = getQueriesLocalStorageKey(questId);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to load queries from localStorage:', error);
    return {};
  }
}

function hasIntro(chapter) {
  if (!chapter) {
    return false;
  }

  return Boolean(chapter.has_intro || (chapter.intro && chapter.intro.trim()));
}

function hasOutro(chapter) {
  if (!chapter) {
    return false;
  }

  return Boolean(chapter.has_outro || (chapter.outro && chapter.outro.trim()));
}

function mergeChapterIntoList(list, chapter) {
  if (!Array.isArray(list) || !chapter || !chapter.id) {
    return list;
  }

  const existingIndex = list.findIndex((item) => item && item.id === chapter.id);
  const updated = [...list];
  updated[existingIndex] = { ...list[existingIndex], ...chapter };

  return updated;
}

// Helper function to build screens from chapters
function buildScreens(chapters, questIntroImageUrl, savedScreenIndex) {
  const screens = [];

  let index = -1;

  index += 1;

  screens.push({
    type: 'questIntro',
    screenIndex: index,
    chapterId: null,
    displayName: 'Prologue',
    chapter: null,
    chapterNumber: '',
    finished: (savedScreenIndex && savedScreenIndex > 0) || chapters.some(chapter => chapter.finished),
    accessible: true,
    introImageUrl: questIntroImageUrl
  });

  chapters.forEach((chapter) => {
    const chapterNumber = (chapter.position !== undefined && chapter.position !== null) ? chapter.position : '';

    if (hasIntro(chapter)) {
      index += 1;

      screens.push({
        type: 'intro',
        screenIndex: index,
        chapterId: chapter.id,
        displayName: `**${chapter.name}**. Intro`,
        chapter: chapter,
        chapterNumber: chapterNumber,
        finished: chapter.finished,
        accessible: chapter.finished
      });
    }

    // Add workspace screen
    index += 1;
    screens.push({
      type: 'workspace',
      screenIndex: index,
      chapterId: chapter.id,
      displayName: `Workspace`,
      chapter: chapter,
      chapterNumber: chapterNumber,
      finished: chapter.finished,
      accessible: chapter.finished
    });

    // Add outro screen if exists
    if (hasOutro(chapter)) {
      index += 1;

      screens.push({
        type: 'outro',
        screenIndex: index,
        chapterId: chapter.id,
        displayName: 'Outro',
        chapter: chapter,
        chapterNumber: chapterNumber,
        finished: chapter.finished,
        accessible: chapter.finished
      });
    }
  });

  // Mark accessible screens according to progression logic
  const unfinishedScreens = screens.filter(s => !s.finished);
  if (unfinishedScreens.length === screens.length && screens.length > 0) {
    // All screens unfinished, mark the first as accessible
    screens[0].accessible = true;
  } else if (unfinishedScreens.length > 0 && unfinishedScreens.length < screens.length) {
    // Some finished, some unfinished
    // Find the last finished screen
    let lastFinishedIndex = -1;
    for (let i = 0; i < screens.length; i++) {
      if (screens[i].finished) {
        lastFinishedIndex = i;
      }
    }
    const nextScreenIndex = lastFinishedIndex + 1;
    if (screens[nextScreenIndex]) {
      screens[nextScreenIndex].accessible = true;
    }
  }

  return screens;
}

const sharedStore = {
  state: {
    currentScreenIndex: 0,
    screens: [],
    questFinished: window.state.questFinished,
    finishPagePath: window.state.finishPagePath,
    currentDurationSec: null,
    currentChapterStartedAt: null,
    hintPenaltySec: 0,
    questId: window.state.questId,
    questSlug: window.state.questSlug,
    questIntro: window.state.questIntro || "",
    questName: window.state.questName || "",
    questIntroImageUrl: window.state.questIntroImageUrl || "",
    username: window.state.username,
    scrollToTopCallback: null,
    chaptersById: {},
    userQueriesById: {},
    userSolutionQueriesById: {},
    pauseRequested: window.state.pauseRequested || false,
    questPaused: window.state.questPaused || false
  },
  setScreens(chapters) {
    this.state.currentDurationSec = chapters.reduce((total, chapter) => {
      return total + (chapter.duration_sec || 0);
    }, 0);

    const currentChapter = chapters.find(chapter => !chapter.duration_sec);
    this.state.currentChapterStartedAt = currentChapter ? new Date(currentChapter.started_at) : null;

    this.state.chaptersById = chapters.reduce((index, chapter) => {
      index[chapter.id] = chapter;
      return index;
    }, {});

    const savedScreenIndex = loadScreenIndexFromCookie(this.state.questSlug);

    this.state.screens = buildScreens(chapters, this.state.questIntroImageUrl, savedScreenIndex);

    // Load queries from LocalStorage
    const savedQueries = loadQueriesFromLocalStorage(this.state.questId);

    chapters.forEach((chapter) => {
      // Use saved query from LocalStorage if available, otherwise use chapter.user_query
      this.state.userQueriesById[chapter.id] = savedQueries[chapter.id] || chapter.user_query;

      // Store solution queries for finished chapters
      if (chapter.user_query) {
        this.state.userSolutionQueriesById[chapter.id] = chapter.user_query;
      }
    });

    // Initialize total hint penalty from all chapters
    this.state.hintPenaltySec = chapters.reduce((total, chapter) => {
      return total + (chapter.hint_penalty_sec || 0);
    }, 0);

    if (savedScreenIndex !== null && savedScreenIndex >= 0 && savedScreenIndex < this.state.screens.length) {
      const savedScreen = this.state.screens[savedScreenIndex];

      if (savedScreenIndex > 0 && savedScreenIndex < this.state.screens.length - 1) {
        const previousScreen = this.state.screens[savedScreenIndex - 1];

        // If the save screen from a cookie is a chapter, we need to finish the
        // previous intro screen, otherwise it'll be shown as active in the sidebar.
        if (savedScreen.type === "workspace" && previousScreen?.accessible) {
          this.state.screens[savedScreenIndex - 1].finished = true;
          this.state.screens[savedScreenIndex].accessible = true;
        }

        if (savedScreen && savedScreen.accessible && previousScreen?.accessible) {
          this.state.currentScreenIndex = savedScreenIndex;
          return;
        }
      }
    } else {
      this.state.currentScreenIndex = findFirstUnfinishedScreenIndex(this.state.screens);
      saveScreenIndexToCookie(this.state.questSlug, this.state.currentScreenIndex);
    }
  },
  setCurrentChapterStartedAt(startedAt) {
    this.state.currentChapterStartedAt = startedAt ? new Date(startedAt) : null;
  },
  updateTimer({ duration, startedAt }) {
    this.state.currentDurationSec = duration;
    this.state.currentChapterStartedAt = startedAt ? new Date(startedAt) : null;
  },
  nextScreen() {
    const currentScreen = this.state.screens[this.state.currentScreenIndex];
    currentScreen.finished = true;

    this.state.currentScreenIndex += 1;

    if (this.state.currentScreenIndex < this.state.screens.length) {
      const newCurrentScreen = this.state.screens[this.state.currentScreenIndex];
      newCurrentScreen.accessible = true;

      saveScreenIndexToCookie(this.state.questSlug, this.state.currentScreenIndex);

      const currentChapterId = currentScreen.chapterId;
      const newChapterId = newCurrentScreen.chapterId;

      if (newChapterId && currentChapterId !== newChapterId) {
        this.startChapter();
      }

      if (this.state.scrollToTopCallback) {
        this.state.scrollToTopCallback();
      }
    } else {
      window.location = this.state.finishPagePath;
    }
  },
  setQuestFinished() {
    this.state.questFinished = true;
  },
  setCurrentScreenIndex(screenIndex) {
    this.state.currentScreenIndex = screenIndex;
    saveScreenIndexToCookie(this.state.questSlug, this.state.currentScreenIndex);
  },
  setScrollToTopCallback(callback) {
    this.state.scrollToTopCallback = callback;
  },
  addHintPenalty(penaltySec) {
    this.state.hintPenaltySec += penaltySec;
  },
  mergeChapter(chapter) {
    if (!chapter || !chapter.id) {
      return;
    }

    const existingChapter = this.state.chaptersById[chapter.id] || {};
    const mergedChapter = { ...existingChapter, ...chapter };

    this.state.chaptersById[chapter.id] = mergedChapter;

    // Store solution query for finished chapters
    if (chapter.user_query) {
      this.state.userSolutionQueriesById[chapter.id] = chapter.user_query;
    }

    this.state.screens = this.state.screens.map((screen) => {
      if (screen.chapterId === chapter.id) {
        return { ...screen, chapter: mergedChapter };
      }

      return screen;
    });

    if (window.state && Array.isArray(window.state.chapters)) {
      window.state.chapters = mergeChapterIntoList(window.state.chapters, mergedChapter);
    }
  },
  mergeChapters(chapters) {
    if (!Array.isArray(chapters)) {
      return;
    }

    chapters.forEach((chapter) => this.mergeChapter(chapter));
  },
  startChapter() {
    const screen = this.state.screens[this.state.currentScreenIndex];

    if (!screen || !screen.chapterId) {
      return;
    }

    const chapterId = screen.chapterId;

    let apiPath = `/api/quests/chapters/${chapterId}/start.json`;

    let formData = new FormData();

    formData.append("chapter_participant_id", this.state.questId);

    axios.patch(apiPath, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      }
    }).then(response => {
      this.updateTimer({
        duration: response.data.current_duration_sec,
        startedAt: response.data.current_chapter_started_at
      });
    }).catch(error => {
      notify({
          message: "Looks like there's a problem starting your quest, so sorry! Please try again or contact support@sqlhabit.com",
          type: "error"
        });
    });
  },
  updateUserQuery(chapterId, query) {
    if (!chapterId) {
      return;
    }

    this.state.userQueriesById[chapterId] = query;
    saveQueriesToLocalStorage(this.state.questId, this.state.userQueriesById);
  },
  setPauseRequested(value) {
    this.state.pauseRequested = value;
  },
  setQuestPaused(value) {
    this.state.questPaused = value;
  },
  setCurrentDurationSec(durationSec) {
    this.state.currentDurationSec = durationSec;
  },
  requestPause() {
    const questSlug = this.state.questSlug;

    return axios.patch(`/api/quests/participants/request_pause.json`, {
      quest_slug: questSlug
    }, {
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      }
    }).then(response => {
      this.state.pauseRequested = true;
      this.state.currentDurationSec = response.data.current_duration_sec;

    }).catch(error => {
      notify({
        message: "Failed to request pause. Please try again.",
        type: "error"
      });
      throw error;
    });
  },
  cancelPause() {
    const questSlug = this.state.questSlug;

    return axios.patch(`/api/quests/participants/cancel_pause.json`, {
      quest_slug: questSlug
    }, {
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      }
    }).then(response => {
      this.state.pauseRequested = false;
      return response;
    }).catch(error => {
      notify({
        message: "Failed to cancel pause. Please try again.",
        type: "error"
      });
      throw error;
    });
  },
  continueQuest() {
    const questSlug = this.state.questSlug;

    // Bumping currentChapterStartedAt to now() to avoid duration jumping back
    this.state.currentChapterStartedAt = new Date();

    return axios.patch(`/api/quests/participants/continue_quest.json`, {
      quest_slug: questSlug
    }, {
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      }
    }).then(response => {
      this.state.questPaused = false;
      this.state.pauseRequested = false;

      const unlockedChapters = response.data.unlocked_chapters || [];
      this.mergeChapters(unlockedChapters);

      this.updateTimer({
        duration: response.data.current_duration_sec,
        startedAt: response.data.current_chapter_started_at
      });

      if (unlockedChapters.length > 0) {
        this.addChapterScreens(unlockedChapters[0]);
      }
    }).catch(error => {
      notify({
        message: "Failed to continue quest. Please try again.",
        type: "error"
      });
      throw error;
    });
  },
  addChapterScreens(chapter) {
    if (!chapter || !chapter.id) {
      return;
    }

    const chapterNumber = (chapter.position !== undefined && chapter.position !== null) ? chapter.position : '';
    let maxIndex = Math.max(...this.state.screens.map(s => s.screenIndex), -1);

    if (hasIntro(chapter)) {
      maxIndex += 1;
      this.state.screens.push({
        type: 'intro',
        screenIndex: maxIndex,
        chapterId: chapter.id,
        displayName: `**${chapter.name}**. Intro`,
        chapter: chapter,
        chapterNumber: chapterNumber,
        finished: false,
        accessible: true
      });
    }

    maxIndex += 1;
    this.state.screens.push({
      type: 'workspace',
      screenIndex: maxIndex,
      chapterId: chapter.id,
      displayName: `Workspace`,
      chapter: chapter,
      chapterNumber: chapterNumber,
      finished: false,
      accessible: !hasIntro(chapter)
    });

    if (hasOutro(chapter)) {
      maxIndex += 1;
      this.state.screens.push({
        type: 'outro',
        screenIndex: maxIndex,
        chapterId: chapter.id,
        displayName: 'Outro',
        chapter: chapter,
        chapterNumber: chapterNumber,
        finished: false,
        accessible: false
      });
    }
  }
}

export default sharedStore;
